import { FormControl, FormHelperText, FormLabel, Grid, IconButton, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useFormikContext } from "formik";
import { crudGetOne, ReduxState, useRecordContext } from "ra-core";
import React, { FC, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { crudGetOneEx } from "~/actions/crudFetchEx";
import { useHasPermit } from "~/authProvider/useHasPermit";
import { MANAGE_OBFUSCATION_RULES } from "~/constants";
import { RecPatch } from "~/dataProvider/tools";
import { ObfuscationDataType, ObfuscationRuleDto, ObfuscationRuleType } from "~/gen/api/encrypt";
import { displayInferredType, inferDataTypeInfo, Inferred } from "./obfsRuleCompatibleMapping";
import { validators } from "./obfsRuleValidators";


interface Values {
  dataType: ObfuscationDataType; 
  ruleType: ObfuscationRuleType; 
  rangeValues: string[];
  rangeBorders: boolean[];
  inferredType: Inferred | null;
}

const resource = 'settings/encryption/obfuscation-rule'

const ValueRangeRuleInput: FC = () => {
  const hasWritePermit = useHasPermit([MANAGE_OBFUSCATION_RULES])
  const record = useRecordContext<ObfuscationRuleDto&RecPatch>()
  const mode = !record ? 'create' : 'edit'

  const { values, touched, errors, setValues, setFieldValue, setFormikState, registerField, unregisterField, validateField } = useFormikContext<Values>()
  const [minVal, maxVal] = values.rangeValues || ['', '']
  const [includeMin, includeMax] = values.rangeBorders || [true, true]

  const effectDispatch = useDispatch()
  useEffect(() => {
    if (mode === 'edit') {
      effectDispatch(crudGetOneEx(resource, record.id, ''))
    }
  }, [effectDispatch, record?.id, mode])

  const initialRangeValueRef = useRef(record?.rangeValues)

  useSelector<ReduxState, any>(store => {
    const obfsRule : ObfuscationRuleDto&RecPatch | undefined = 
      !!record  
      ? store.admin.resources[resource]?.data[record.id]
      : undefined
    if (obfsRule && obfsRule.rangeValues !== initialRangeValueRef.current) {
      initialRangeValueRef.current = obfsRule.rangeValues
      setValues(v => ({
        ...v,
        rangeValues: obfsRule.rangeValues as any,
        rangeBorders: obfsRule.rangeBorders || [false, false],
        inferredType: (obfsRule.inferredType || null) as any,
      }))
    }
  })

  const toggleInclude = (border: 'min'|'max') => {
    setFieldValue('rangeBorders',
      border === 'min'
      ? [!includeMin, includeMax]
      : [includeMin, !includeMax]
    )
  }
  const {current: rangeError} = useRef({ minError:false, maxError:false, errorMsg: '', prevVal: [] as any })
  
  const rangeValuesValidator = useMemo(() => {
    const valMapper =
      values.dataType === ObfuscationDataType.Integer ? (x: string) => parseInt(x)
      : values.dataType === ObfuscationDataType.Numeric ? (x: string) => parseFloat(x)
      : values.dataType === ObfuscationDataType.DateTime ? (x: string) => new Date(x)
      : (x: string) => x
    const validator = validators[values.dataType]
    return (rangeValues: [string, string]) => {
      if (rangeError.prevVal === rangeValues) {
        return rangeError.errorMsg || undefined
      }
      rangeError.prevVal = rangeValues
      const [minVal, maxVal] = rangeValues
      
      rangeError.minError = !!minVal && !validator(minVal)
      rangeError.maxError = !!maxVal && !validator(maxVal)
      rangeError.errorMsg = 
        !minVal ? 'Range is required'
        : rangeError.minError ? 'Invalid Min-Value'
        : !maxVal ? 'Range is required'
        : rangeError.maxError ? 'Invalid Max-Value'
        : valMapper(minVal) > valMapper(maxVal) ? 'Min-Value should be smaller then Max-Value'
        : ''
      return rangeError.errorMsg || undefined
    }
  }, [rangeError, values.dataType])
  
  useEffect(() => {
    registerField('rangeValues', { validate: rangeValuesValidator })
    rangeError.prevVal = []
    validateField('rangeValues')
    setTimeout(() => {
      setFormikState((states) => {
        if (states.errors.rangeValues) {
          return states
        } else {
          return {
            ...states,
            values: {
              ...states.values,
              inferredType: inferDataTypeInfo(values.dataType, states.values.rangeValues) || null,
            }
          }
        }
      })
    })
    return () => {
      unregisterField('rangeValues')
    }
  }, [registerField, unregisterField, rangeValuesValidator])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isMinVal = e.target.id === 'range_min_val'
    const rangeValues = isMinVal 
      ? [e.target.value, maxVal || '']
      : [minVal || '', e.target.value]

    rangeValuesValidator(rangeValues as any)
    setFormikState((states) => {
      const { values, errors: errors0, touched } = states
      const errors = { ...errors0 }
      if (!rangeError.errorMsg) { delete errors.rangeValues } 
      else { errors.rangeValues = rangeError.errorMsg }
      
      return {
        ...states,
        values: {
          ...values,
          rangeValues,
          inferredType: (!rangeError.errorMsg && 
            inferDataTypeInfo(values.dataType, rangeValues)) 
            || null,
        },
        errors,
        touched: {
          ...touched,
          rangeValues: {
            ...(touched.rangeValues as any),
            [isMinVal ? 'minVal' : 'maxVal']: true,
          }
        },
      }
    })
  }


  return (
    <>
      <Grid item xs={12}>
        <FormControl component="fieldset" sx={{ width: 'calc(100% - 32px)', mx: 2 }}
          error={Boolean(touched.rangeValues && errors.rangeValues)}
        >
          <FormLabel component="legend" sx={{ mb: 1 }}>Range</FormLabel>
          <Stack 
            direction="row" 
            sx={{
              '& .MuiTextField-root': {
                flex: '1 1 auto',
              },
              '& .MuiIconButton-root': {
                mt: 0.5,
              },
            }}
            spacing={2}
          >
            <Tooltip title={`${!includeMin ? 'Not ' : ''}Include Min-Value`}>
              <IconButton color={includeMin ? 'primary' : undefined} onClick={() => hasWritePermit && toggleInclude('min')}>
                <Typography variant="h5">{includeMin ? '[' : '('}</Typography>
              </IconButton>
            </Tooltip>
            <TextField 
              disabled={!hasWritePermit}
              id="range_min_val" 
              label="Min-Value" 
              variant="standard" 
              error={(touched.rangeValues as any)?.minVal && rangeError.minError}
              value={minVal || ''}
              onChange={handleChange}
            />
            <TextField 
              disabled={!hasWritePermit}
              id="range_max_val" 
              label="Max-Value" 
              variant="standard" 
              error={(touched.rangeValues as any)?.maxVal && rangeError.maxError}
              value={maxVal || ''}
              onChange={handleChange}
            />
            <Tooltip title={`${!includeMax ? 'Not ' : ''}Include Max-Value`}>
              <IconButton color={includeMax ? 'primary' : undefined} onClick={() => hasWritePermit && toggleInclude('max')}>
                <Typography variant="h5">{includeMax ? ']' : ')'}</Typography>
              </IconButton>
            </Tooltip>
          </Stack>
          {Boolean(touched.rangeValues && errors.rangeValues) && <FormHelperText>{touched.rangeValues && errors.rangeValues}</FormHelperText>}
        </FormControl>
        <FormHelperText sx={{ mt: 2, ml: 4 }}>
          {displayInferredType(values.inferredType)}
        </FormHelperText>
      </Grid>
    </>
  )
}

export default ValueRangeRuleInput;
